/**
 * @file App.tsx
 * @description
 * The root component of the React application. Sets up context providers
 * for authentication and socket communication, and configures client-side routing
 * using react-router-dom. Handles initial authentication check and redirection.
 *
 * This file defines the main structure and available routes:
 * - AuthProvider and SocketProvider wrap the application.
 * - AuthRedirect component handles global routing based on authentication state.
 * - Specific routes (/login, /wrong-passcode, /lobby, /game/:code) are defined.
 *
 * @dependencies
 * - react: Standard React library.
 * - react-router-dom: For client-side routing and navigation.
 * - AuthProvider, SocketProvider, useAuth: Contexts and hook for application-wide state.
 * - Login, WrongPasscode, Lobby, GameRoom: Page components for different routes.
 * - App.css: Global or base styles.
 */

import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/components.css';

import { AuthProvider, useAuth } from './contexts/AuthContext'; // Import AuthProvider and useAuth hook
import { SocketProvider } from './contexts/SocketContext'; // Import SocketProvider
import Login from './pages/Login';
import WrongPasscode from './pages/WrongPasscode'; // Import the new WrongPasscode page
import Lobby from './pages/Lobby';
import GameRoom from './pages/GameRoom'; // Import the new GameRoom component

/**
 * Helper component to handle initial redirection based on auth status.
 * Must be used inside AuthProvider.
 * It renders nothing itself but uses side effects (useEffect) and Navigate
 * components to control routing based on the `isAuthenticated` state.
 */
const AuthRedirect: React.FC = () => {
  // Use the custom hook to access the context
  const { isAuthenticated, checkAuth } = useAuth();
  const location = useLocation();

  // On mount or when isAuthenticated becomes null (initial state), perform the check
  useEffect(() => {
    console.log('AuthRedirect Effect: isAuthenticated state:', isAuthenticated);
    if (isAuthenticated === null) {
        console.log('AuthRedirect Effect: Auth state is null, performing checkAuth...');
        checkAuth();
    }
     // Add logging for auth state changes for debugging
     if (isAuthenticated !== null) {
         console.log('AuthRedirect Effect: Auth state resolved to:', isAuthenticated);
     }
  }, [isAuthenticated, checkAuth]); // Depend on isAuthenticated state and the checkAuth function

  // While checking, show a loading indicator for the entire page
  if (isAuthenticated === null) {
    console.log('AuthRedirect: Rendering loading indicator.');
    // Apply some basic styling or a spinner class if available
    return <div className="flex items-center justify-center h-screen text-gray-900 dark:text-gray-100">Loading authentication status...</div>;
  }

  // Define routes that do NOT require authentication
  const publicRoutes = ['/login', '/wrong-passcode'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // If authenticated:
  // - If currently on a public route or the root path, redirect to lobby.
  // - If on a protected route, return null to let Routes below render it.
  if (isAuthenticated === true) {
     if (isPublicRoute || location.pathname === '/') {
        console.log('AuthRedirect: Authenticated, redirecting from public/root path to lobby.');
        return <Navigate to="/lobby" replace />;
     }
     // If already on a protected route (/lobby, /game/*), let Routes handle it.
     console.log('AuthRedirect: Authenticated, staying on protected path:', location.pathname);
     return null; // Let Routes render the content
  }

  // If NOT authenticated:
  // - If currently on a protected route, redirect to login.
  // - If on a public route, return null to let Routes below render it (e.g., Login page).
   if (isAuthenticated === false) {
      if (!isPublicRoute) {
        console.log(`AuthRedirect: Not authenticated, redirecting from protected path (${location.pathname}) to login.`);
        return <Navigate to="/login" replace />;
      }
       // If already on a public route (/login, /wrong-passcode), let Routes handle it.
       console.log('AuthRedirect: Not authenticated, staying on public path:', location.pathname);
       return null; // Let Routes render the content
   }


  // This line should theoretically not be reached if isAuthenticated is either null, true, or false.
  // Adding a default return for safety, though it might indicate a logic flaw if hit.
  console.warn('AuthRedirect: Reached end of logic without handling isAuthenticated state:', isAuthenticated);
   return null;
};


function App() {
  return (
    <Router> {/* Wrap the application with the Router */}
      {/* AuthProvider should wrap everything that needs auth context, including SocketProvider */}
      <AuthProvider>
        {/* SocketProvider needs access to auth context, so it's inside AuthProvider */}
        {/* SocketProvider should only attempt to connect if isAuthenticated is true, handled within the provider */}
        <SocketProvider>
          <div className="App"> {/* Apply a global App class for potential base styling */}
            {/* AuthRedirect handles initial authentication checks and redirects */}
            {/* It should be placed before Routes so it can perform redirects before route matching */}
            <AuthRedirect />

            {/* Define application routes */}
            <Routes>
              {/*
                The root path is primarily handled by AuthRedirect. If authenticated, it redirects to /lobby.
                If not authenticated, AuthRedirect redirects to /login.
                This route definition as a Navigate is a fallback/explicit statement for the root.
              */}
              <Route path="/" element={<Navigate to="/login" replace />} />


              {/* Login/Passcode entry page - accessible when not authenticated */}
              <Route path="/login" element={<Login />} />

              {/* Wrong Passcode page - accessible when not authenticated */}
              <Route path="/wrong-passcode" element={<WrongPasscode />} />

              {/*
                Protected routes: Lobby and GameRoom.
                AuthRedirect will automatically redirect to /login if isAuthenticated is false
                when trying to access these paths. If isAuthenticated is true, AuthRedirect
                returns null, allowing the Routes here to match and render the components.
              */}
               {/* Render Lobby and GameRoom components only if AuthRedirect doesn't redirect */}
               {/* Note: The component rendered by the Route still needs to check isAuthenticated internally
                   if it has logic that depends on the authenticated state (e.g., fetching data). */}
              <Route path="/lobby" element={<Lobby />} />
              <Route path="/game/:code" element={<GameRoom />} />

              {/* Add a catch-all route for 404 if needed */}
              {/* <Route path="*" element={<div>404 Not Found</div>} /> */}
            </Routes>
          </div>
        </SocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;